import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import type { User } from '@supabase/supabase-js';

export type UserRole = 'admin' | 'contributor' | 'user' | null;

interface RoleCheckResult {
  role: UserRole;
  isAdmin: boolean;
  isContributor: boolean;
  isAdminOrContributor: boolean;
  loading: boolean;
  user: User | null;
}

// Fail-open timeout helper to prevent infinite loading on slow networks
const withTimeout = <T,>(promise: Promise<T>, ms: number): Promise<T> => {
  return new Promise<T>((resolve, reject) => {
    const id = setTimeout(() => reject(new Error('timeout')), ms);
    promise.then(
      (value) => {
        clearTimeout(id);
        resolve(value);
      },
      (err) => {
        clearTimeout(id);
        reject(err);
      }
    );
  });
};

const REQUEST_TIMEOUT_MS = 30000; // Increased from 12s to 30s for slow connections

/**
 * Hook to check user's role and permissions
 * @returns RoleCheckResult with role information and permission flags
 */
export const useRole = (): RoleCheckResult => {
  const [role, setRole] = useState<UserRole>(null);
  const [loading, setLoading] = useState(true);
  // Single source of truth: get user from AuthContext
  const { user, loading: authLoading } = useAuth();

  // React to user changes from AuthContext (single source of truth)
  useEffect(() => {
    // Wait for auth to finish loading
    if (authLoading) {
      return;
    }

    if (user) {
      checkRole();
    } else {
      setRole(null);
      setLoading(false);
    }
  }, [user, authLoading]);

  const checkRole = async () => {
    try {
      setLoading(true);

      // User comes from AuthContext (single source of truth)
      if (!user) {
        setRole(null);
        setLoading(false);
        return;
      }

			// Fetch all roles for this user and resolve effective role by priority
			const { data: rolesData, error: rolesError } = await withTimeout(
        supabase
          .from('user_roles')
          .select('role')
					.eq('user_id', user.id),
        REQUEST_TIMEOUT_MS
      );

			if (rolesError) {
				console.error('Error fetching user roles:', rolesError);
				// If there's an error fetching roles, default to 'user' role
				// This prevents the app from being stuck in loading state
				setRole('user');
				setLoading(false);
				return;
      }

			const roles = (rolesData ?? []).map(r => (r as { role: string }).role);
			let effectiveRole: UserRole = 'user';
			if (roles.includes('admin')) {
				effectiveRole = 'admin';
			} else if (roles.includes('contributor')) {
				effectiveRole = 'contributor';
			} else if (roles.includes('user')) {
				effectiveRole = 'user';
			}
			setRole(effectiveRole);
    } catch (error) {
      console.error('Error checking role:', error);
      // Default to 'user' role on error to prevent infinite loading
      setRole('user');
    } finally {
      setLoading(false);
    }
  };

  return {
    role,
    isAdmin: role === 'admin',
    isContributor: role === 'contributor',
    isAdminOrContributor: role === 'admin' || role === 'contributor',
    loading,
    user,
  };
};

/**
 * Check if a user has a specific role or higher
 * @param requiredRole The minimum required role
 * @returns Promise<boolean>
 */
export const hasRole = async (requiredRole: 'admin' | 'contributor' | 'user'): Promise<boolean> => {
  try {
    const { data: { user } } = await supabase.auth.getUser();
    
    if (!user) return false;

		const { data: rolesData, error: rolesError } = await supabase
      .from('user_roles')
      .select('role')
			.eq('user_id', user.id);

		if (rolesError) {
			console.error('Error checking role:', rolesError);
			// If we can't determine, allow 'user' only
			return requiredRole === 'user';
		}

		const roles = (rolesData ?? []).map(r => (r as { role: string }).role);
		const isAdmin = roles.includes('admin');
		const isContributor = roles.includes('contributor');
    
		if (requiredRole === 'admin') return isAdmin;
		if (requiredRole === 'contributor') return isAdmin || isContributor;
		// 'user' has access by default even if no explicit row exists
		return true;
  } catch (error) {
    console.error('Error checking role:', error);
    return false;
  }
};

